import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { getDocumentName, graphql } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';
import { memoForm2ApiMapper } from '../../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { MissionPage_OneMission_QueryDocument } from '../../Mission/hooks/useFindOneMissionQuery.hook';
import type { MemosState } from '../../../forms/MemoBeforeSubmit';
import type { BackError } from '../../../utils/back-errors.util';
import type { OptionGroupsSelectionControl } from '../../../components/ServiceUsages/types/option-groups-selection-control.type';
import { MessageList } from '../../../components/MessageList';

import { MissionPage_SubMissionsPricing_QueryDocument } from './useSubMissionsPricing.hook';

export const MissionPage_UpdateRejectedOptionGroups_MutationDocument = graphql(`
  mutation MissionPage_UpdateRejectedOptionGroups_Mutation(
    $serviceUserId: Id!
    $rejectedOptionGroupIds: [Id!]!
    $versionData: VersionDataInput
  ) {
    updateMissionServicesInfoRejectedOptionGroups(
      serviceUserId: $serviceUserId
      rejectedOptionGroupIds: $rejectedOptionGroupIds
      versionData: $versionData
    ) {
      _id
    }
  }
`);

// The choice of a collaboration is shown by its sub-missions and the other
// way round: every mission pricing is refreshed.
const QUERY_KEYS_TO_INVALIDATE = [
  [getDocumentName(MissionPage_OneMission_QueryDocument)],
  [getDocumentName(MissionPage_SubMissionsPricing_QueryDocument)],
] as const;

interface Params {
  readonly isEditable: boolean;
  readonly missionId: Id;
}

export const useOptionGroupsSelection = ({
  isEditable,
  missionId,
}: Readonly<Params>): OptionGroupsSelectionControl => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync, isPending } = useGraphQLMutation(
    MissionPage_UpdateRejectedOptionGroups_MutationDocument,
  );

  const onChange = useCallback(
    async (
      rejectedOptionGroupIds: readonly Id[],
      memos: Readonly<MemosState>,
    ) => {
      try {
        await mutateAsync({
          serviceUserId: missionId,
          rejectedOptionGroupIds,
          versionData: memoForm2ApiMapper(memos),
        });
      } catch (error) {
        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
          },
        );

        return;
      }

      await Promise.all(
        QUERY_KEYS_TO_INVALIDATE.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
      enqueueSnackbar('Les options retenues sont enregistrées.');
    },
    [enqueueSnackbar, missionId, mutateAsync, queryClient],
  );

  return useMemo(
    () => ({ isDisabled: !isEditable, isLoading: isPending, onChange }),
    [isEditable, isPending, onChange],
  );
};
